if (!customElements.get('category-carousel')) {
  class CategoryCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.js-cc-track');
      if (!this.track) return;

      this.items = [...this.track.children];
      this.prevBtn = this.querySelector('.js-cc-prev');
      this.nextBtn = this.querySelector('.js-cc-next');
      this.dotsWrap = this.querySelector('.js-cc-dots');
      this.rtl = document.dir === 'rtl';
      this.pages = 0;

      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
      if (this.dotsWrap) {
        this.dotsWrap.addEventListener('click', (evt) => {
          const dot = evt.target.closest('.category-carousel__dot');
          if (dot) this.goTo(Number(dot.dataset.index));
        });
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.update);
      }

      if (window.matchMedia('(pointer: fine)').matches) this.initDrag();

      // Editor de temas: muestra la tarjeta seleccionada.
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.update);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    /** Distancia entre el inicio de una tarjeta y la siguiente (ancho + espacio). */
    get span() {
      if (this.items.length > 1) {
        return Math.abs(this.items[1].offsetLeft - this.items[0].offsetLeft);
      }
      return this.items[0] ? this.items[0].offsetWidth : 0;
    }

    get scrollPos() {
      return Math.abs(this.track.scrollLeft);
    }

    update() {
      const { span } = this;
      if (!span) return;

      const gap = span - this.items[0].offsetWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap + 2) / span));
      const pages = Math.max(1, this.items.length - perView + 1);
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      this.toggleAttribute('data-static', maxScroll <= 2);

      if (pages !== this.pages) {
        this.pages = pages;
        this.renderDots();
      }
      this.setActive();
    }

    renderDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      if (this.pages < 2) return;
      for (let i = 0; i < this.pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'category-carousel__dot';
        dot.dataset.index = i;
        dot.tabIndex = -1;
        this.dotsWrap.appendChild(dot);
      }
    }

    get currentIndex() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (this.scrollPos >= maxScroll - 2) return this.pages - 1;
      return Math.min(this.pages - 1, Math.round(this.scrollPos / this.span));
    }

    setActive() {
      const index = this.currentIndex;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      if (this.dotsWrap) {
        [...this.dotsWrap.children].forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      }
      if (this.prevBtn) this.prevBtn.disabled = this.scrollPos <= 2;
      if (this.nextBtn) this.nextBtn.disabled = this.scrollPos >= maxScroll - 2;
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.setActive();
        this.ticking = false;
      });
    }

    goTo(index) {
      const left = Math.max(0, index) * this.span;
      this.track.scrollTo({ left: this.rtl ? -left : left, behavior: 'smooth' });
    }

    step(direction) {
      this.goTo(this.currentIndex + direction);
    }

    onBlockSelect(evt) {
      const item = this.items.find((el) => el.contains(evt.target) || el === evt.target);
      if (item) this.goTo(this.items.indexOf(item));
    }

    /** Arrastrar con el mouse en escritorio. */
    initDrag() {
      let startX = 0;
      let startScroll = 0;
      let down = false;
      let moved = false;

      this.track.addEventListener('dragstart', (evt) => evt.preventDefault());
      this.track.addEventListener('mousedown', (evt) => {
        if (this.hasAttribute('data-static')) return;
        down = true;
        moved = false;
        startX = evt.pageX;
        startScroll = this.track.scrollLeft;
      });
      this.track.addEventListener('mousemove', (evt) => {
        if (!down) return;
        const dx = evt.pageX - startX;
        if (!moved && Math.abs(dx) > 8) {
          moved = true;
          this.track.classList.add('is-dragging');
        }
        if (moved) this.track.scrollLeft = startScroll - dx;
      });
      const blockClick = (e) => e.preventDefault();
      const end = (evt) => {
        if (!down) return;
        down = false;
        if (moved) {
          this.track.classList.remove('is-dragging');
          this.goTo(this.currentIndex);
          if (evt.type === 'mouseup') {
            // Evita que el clic al soltar abra el enlace.
            this.track.addEventListener('click', blockClick, { capture: true, once: true });
            setTimeout(() => this.track.removeEventListener('click', blockClick, { capture: true }), 100);
          }
        }
      };
      this.track.addEventListener('mouseup', end);
      this.track.addEventListener('mouseleave', end);
    }
  }

  customElements.define('category-carousel', CategoryCarousel);
}
